/**
 * Hook for offline-first ICS form operations.
 *
 * When online: fetches forms from server via React Query, caches to IndexedDB.
 * When offline: reads from IndexedDB cache.
 * Auto-syncs unsynced forms when coming back online.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formOfflineService, FormInput, SyncResult } from '../offline/form-offline.service';
import { OfflineForm, CachedFormTemplate } from '../offline/db';
import { useTenantStore } from '@/store/tenant.store';
import { useIncidentStore } from '@/store/incident.store';
import { useAuthStore } from '@/store/auth.store';
import { networkService } from '@/services/network.service';
import { API_URL } from '@/services/api';
import { clientModeHeaders } from '@/services/client-mode';

interface UseOfflineFormsResult {
  forms: OfflineForm[];
  templates: CachedFormTemplate[];
  isLoading: boolean;
  isOffline: boolean;
  isSyncing: boolean;
  unsyncedCount: number;
  lastSyncResult: SyncResult | null;
  syncNow: () => Promise<SyncResult>;
  createForm: (input: FormInput) => Promise<OfflineForm>;
  updateForm: (
    localId: number,
    updates: Partial<Pick<OfflineForm, 'title' | 'form_data' | 'status'>>
  ) => Promise<OfflineForm | null>;
  refresh: () => Promise<void>;
}

export function useOfflineForms(): UseOfflineFormsResult {
  const { activeTenant } = useTenantStore();
  const { activeIncident } = useIncidentStore();
  const { token } = useAuthStore();

  const [forms, setForms] = useState<OfflineForm[]>([]);
  const [templates, setTemplates] = useState<CachedFormTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(networkService.isOnline());
  const [isSyncing, setIsSyncing] = useState(false);
  const [unsyncedCount, setUnsyncedCount] = useState(0);
  const [lastSyncResult, setLastSyncResult] = useState<SyncResult | null>(null);
  const autoSyncAttempted = useRef(false);

  // Track online status
  useEffect(() => {
    const removeListener = networkService.addListener((connected) => {
      setIsOnline(connected);
    });
    return () => {
      removeListener();
    };
  }, []);

  // Fetch forms from server when online
  const { data: serverForms } = useQuery<{
    forms: Array<{
      id: string;
      template_id: string;
      title: string;
      incident_id: string | null;
      tenant_id: string;
      form_data: Record<string, unknown>;
      status: string;
      created_at: string;
      updated_at: string;
      template?: { name?: string; code?: string };
    }>;
  }>({
    queryKey: [
      'offline-forms',
      activeTenant?.id,
      activeIncident?.id,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '100', page: '1' });
      if (activeIncident?.id) params.append('incident_id', activeIncident.id);

      const res = await fetch(`${API_URL}/api/forms?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-ID': activeTenant?.id || '',
          ...clientModeHeaders(),
        },
      });
      if (!res.ok) throw new Error('Failed to fetch forms');
      return res.json();
    },
    enabled: !!token && !!activeTenant && isOnline,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  // Fetch templates from server when online
  const { data: serverTemplates } = useQuery<{
    data: Array<{
      id: string;
      tenant_id: string | null;
      code: string;
      name: string;
      description: string | null;
      category: string | null;
      field_schema: Record<string, unknown>;
      is_active: boolean;
      sort_order: number;
    }>;
  }>({
    queryKey: ['offline-form-templates', activeTenant?.id],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/forms/templates`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tenant-ID': activeTenant?.id || '',
          ...clientModeHeaders(),
        },
      });
      if (!res.ok) throw new Error('Failed to fetch templates');
      return res.json();
    },
    enabled: !!token && !!activeTenant && isOnline,
    staleTime: 5 * 60_000, // Templates change rarely
  });

  // Cache server forms to IndexedDB when data arrives
  useEffect(() => {
    if (serverForms?.forms) {
      formOfflineService
        .cacheFromServer(serverForms.forms)
        .then(() => loadFromCache())
        .catch((err) =>
          console.warn('[useOfflineForms] Failed to cache forms:', err)
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverForms]);

  // Cache templates to IndexedDB when data arrives
  useEffect(() => {
    if (serverTemplates?.data) {
      formOfflineService
        .cacheTemplates(serverTemplates.data)
        .then(() => loadTemplatesFromCache())
        .catch((err) =>
          console.warn('[useOfflineForms] Failed to cache templates:', err)
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverTemplates]);

  // Load forms from IndexedDB
  const loadFromCache = useCallback(async () => {
    setIsLoading(true);
    try {
      const cached = await formOfflineService.getAll(activeIncident?.id);
      setForms(cached);
      const count = await formOfflineService.getUnsyncedCount();
      setUnsyncedCount(count);
    } catch (err) {
      console.error('[useOfflineForms] Failed to load from cache:', err);
    } finally {
      setIsLoading(false);
    }
  }, [activeIncident?.id]);

  // Load templates from IndexedDB
  const loadTemplatesFromCache = useCallback(async () => {
    try {
      const cached = await formOfflineService.getCachedTemplates();
      setTemplates(cached);
    } catch (err) {
      console.error('[useOfflineForms] Failed to load templates:', err);
    }
  }, []);

  // Initial load from cache
  useEffect(() => {
    loadFromCache();
    loadTemplatesFromCache();
  }, [loadFromCache, loadTemplatesFromCache]);

  // Reset auto-sync gate when going offline
  useEffect(() => {
    if (!isOnline) {
      autoSyncAttempted.current = false;
    }
  }, [isOnline]);

  // Auto-sync when coming online
  useEffect(() => {
    if (isOnline && unsyncedCount > 0 && !isSyncing && !autoSyncAttempted.current) {
      autoSyncAttempted.current = true;
      setIsSyncing(true);
      formOfflineService
        .syncAll()
        .then((result) => {
          setLastSyncResult(result);
          return loadFromCache().catch(() => {/* cache refresh failure is non-fatal */});
        })
        .catch(() => {/* auto-sync failure is non-fatal, user can retry manually */})
        .finally(() => {
          setIsSyncing(false);
          // Reset auto-sync gate after a delay so new items created while online can trigger auto-sync
          setTimeout(() => { autoSyncAttempted.current = false; }, 5_000);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, unsyncedCount, isSyncing]);

  // Manual sync
  const syncNow = useCallback(async (): Promise<SyncResult> => {
    if (!networkService.isOnline()) {
      return {
        success: false,
        synced: 0,
        failed: 0,
        errors: ['No network connection'],
      };
    }

    setIsSyncing(true);
    try {
      const result = await formOfflineService.syncAll();
      setLastSyncResult(result);
      await loadFromCache();
      return result;
    } finally {
      setIsSyncing(false);
    }
  }, [loadFromCache]);

  // Create form
  const createForm = useCallback(
    async (input: FormInput): Promise<OfflineForm> => {
      if (!activeTenant) throw new Error('No active tenant');

      const form = await formOfflineService.create(activeTenant.id, {
        ...input,
        incident_id: input.incident_id ?? activeIncident?.id ?? null,
      });

      await loadFromCache();
      return form;
    },
    [activeTenant, activeIncident, loadFromCache]
  );

  // Update form
  const updateForm = useCallback(
    async (
      localId: number,
      updates: Partial<Pick<OfflineForm, 'title' | 'form_data' | 'status'>>
    ): Promise<OfflineForm | null> => {
      const updated = await formOfflineService.update(localId, updates);
      await loadFromCache();
      return updated;
    },
    [loadFromCache]
  );

  // Refresh from cache (and server if online)
  const refresh = useCallback(async () => {
    await loadFromCache();
    await loadTemplatesFromCache();
  }, [loadFromCache, loadTemplatesFromCache]);

  return {
    forms,
    templates,
    isLoading,
    isOffline: !isOnline,
    isSyncing,
    unsyncedCount,
    lastSyncResult,
    syncNow,
    createForm,
    updateForm,
    refresh,
  };
}
